import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { theme } from '../theme';
import { useIsMobile } from '../hooks/useMediaQuery';

let _localIdCounter = 0;
const mkLocalId = () => `l_${++_localIdCounter}_${Date.now()}`;

const DISPLAY_TYPES = ['TextHeading', 'TextSubheading', 'TextBody', 'Image'];
const INPUT_TYPES = ['TextInput', 'TextArea', 'Dropdown', 'RadioButtonsGroup', 'CheckboxGroup', 'DatePicker', 'OptIn'];

const COMPONENT_DEFS = [
  { type: 'TextHeading',       category: 'display', label: 'Encabezado',          defaults: { text: 'Título' } },
  { type: 'TextSubheading',    category: 'display', label: 'Subtítulo',            defaults: { text: 'Subtítulo' } },
  { type: 'TextBody',          category: 'display', label: 'Párrafo',              defaults: { text: 'Texto del mensaje' } },
  { type: 'Image',             category: 'display', label: 'Imagen',               defaults: { src: '', height: 130 } },
  { type: 'TextInput',         category: 'input',   label: 'Campo de texto',       defaults: { name: 'campo', label: 'Campo', input_type: 'text', required: true } },
  { type: 'TextArea',          category: 'input',   label: 'Área de texto',        defaults: { name: 'campo', label: 'Campo', required: true, max_length: 1000 } },
  { type: 'Dropdown',          category: 'input',   label: 'Desplegable',          defaults: { name: 'campo', label: 'Elige una opción', required: true, options: [{ id: '1', title: 'Opción 1' }, { id: '2', title: 'Opción 2' }] } },
  { type: 'RadioButtonsGroup', category: 'input',   label: 'Selección única',      defaults: { name: 'campo', label: 'Elige', required: true, options: [{ id: '1', title: 'Opción 1' }, { id: '2', title: 'Opción 2' }] } },
  { type: 'CheckboxGroup',     category: 'input',   label: 'Selección múltiple',   defaults: { name: 'campo', label: 'Selecciona', required: true, options: [{ id: '1', title: 'Opción 1' }, { id: '2', title: 'Opción 2' }] } },
  { type: 'DatePicker',        category: 'input',   label: 'Selector de fecha',    defaults: { name: 'fecha', label: 'Selecciona una fecha', required: true } },
  { type: 'OptIn',             category: 'input',   label: 'Aceptación',           defaults: { name: 'optin', label: 'Acepto los términos y condiciones', required: true } },
  { type: 'Footer',            category: 'action',  label: 'Botón enviar',         defaults: { label: 'Continuar', next_screen: '' } },
];

function buildMetaComponent(comp) {
  const { type, props } = comp;
  switch (type) {
    case 'TextHeading':       return { type: 'TextHeading', text: props.text || '' };
    case 'TextSubheading':    return { type: 'TextSubheading', text: props.text || '' };
    case 'TextBody':          return { type: 'TextBody', text: props.text || '' };
    case 'Image':             return { type: 'Image', src: props.src || '', height: props.height || 130, 'scale-type': 'contain' };
    case 'TextInput':         return { type: 'TextInput', name: props.name || 'campo', label: props.label || 'Campo', 'input-type': props.input_type || 'text', required: props.required !== false };
    case 'TextArea':          return { type: 'TextArea', name: props.name || 'campo', label: props.label || 'Campo', required: props.required !== false, 'max-length': props.max_length || 1000 };
    case 'Dropdown':          return { type: 'Dropdown', name: props.name || 'campo', label: props.label || 'Campo', required: props.required !== false, 'data-source': (props.options || []).map(o => ({ id: o.id, title: o.title })) };
    case 'RadioButtonsGroup': return { type: 'RadioButtonsGroup', name: props.name || 'campo', label: props.label || 'Campo', required: props.required !== false, 'data-source': (props.options || []).map(o => ({ id: o.id, title: o.title })) };
    case 'CheckboxGroup':     return { type: 'CheckboxGroup', name: props.name || 'campo', label: props.label || 'Campo', required: props.required !== false, 'data-source': (props.options || []).map(o => ({ id: o.id, title: o.title })) };
    case 'DatePicker':        return { type: 'DatePicker', name: props.name || 'fecha', label: props.label || 'Fecha', required: props.required !== false };
    case 'OptIn':             return { type: 'OptIn', name: props.name || 'optin', label: props.label || 'Acepto', required: props.required !== false };
    case 'Footer': {
      const action = props.next_screen
        ? { name: 'navigate', next: { type: 'screen', name: props.next_screen } }
        : { name: 'complete', payload: {} };
      return { type: 'Footer', label: props.label || 'Continuar', 'on-click-action': action };
    }
    default: return { type };
  }
}

function generateFlowJson(screens) {
  return {
    version: '6.0',
    data_api_version: '3.0',
    routing_model: {},
    screens: screens.map((screen, idx) => {
      const isLast = idx === screens.length - 1;
      const displayComps = screen.components.filter(c => DISPLAY_TYPES.includes(c.type));
      const inputComps = screen.components.filter(c => INPUT_TYPES.includes(c.type));
      const footerComp = screen.components.find(c => c.type === 'Footer');
      const layoutChildren = [];
      displayComps.forEach(c => layoutChildren.push(buildMetaComponent(c)));
      if (inputComps.length > 0) {
        const formChildren = [
          ...inputComps.map(c => buildMetaComponent(c)),
          ...(footerComp ? [buildMetaComponent(footerComp)] : []),
        ];
        layoutChildren.push({ type: 'Form', name: `form_${screen.id.toLowerCase()}`, children: formChildren });
      } else if (footerComp) {
        layoutChildren.push(buildMetaComponent(footerComp));
      }
      return {
        id: screen.id,
        title: screen.title,
        terminal: isLast,
        ...(isLast ? { success: true } : {}),
        data: {},
        layout: { type: 'SingleColumnLayout', children: layoutChildren },
      };
    }),
  };
}

function extractVariants(screens) {
  const variants = {};
  screens.forEach(screen => {
    screen.components.forEach(c => {
      if (INPUT_TYPES.includes(c.type) && c.props.name) {
        variants[c.props.name] = '';
      }
    });
  });
  return variants;
}

function createScreen(idx) {
  return { id: `SCREEN_${idx + 1}`, title: `Pantalla ${idx + 1}`, components: [] };
}

function metaNodeToProps(node) {
  const p = {};
  if (node.text !== undefined) p.text = node.text;
  if (node.src) p.src = node.src;
  if (node.height) p.height = node.height;
  if (node.name) p.name = node.name;
  if (node.label) p.label = node.label;
  if (node['input-type']) p.input_type = node['input-type'];
  if (node.required !== undefined) p.required = node.required;
  if (node['max-length']) p.max_length = node['max-length'];
  if (node['data-source']) p.options = node['data-source'].map(o => ({ id: o.id, title: o.title }));
  if (node['on-click-action']) p.next_screen = node['on-click-action']?.next?.name || '';
  return p;
}

function extractComponentsFromScreen(screen) {
  const comps = [];
  const children = screen.layout?.children || [];
  children.forEach(child => {
    if (child.type === 'Form') {
      (child.children || []).forEach(fc => {
        comps.push({ localId: mkLocalId(), type: fc.type, props: metaNodeToProps(fc) });
      });
    } else {
      comps.push({ localId: mkLocalId(), type: child.type, props: metaNodeToProps(child) });
    }
  });
  return comps;
}

function PropsPanel({ comp, onChange, screens, currentScreenId }) {
  const { type, props } = comp;
  const fieldSt = { marginBottom: '8px' };
  const lblSt = { display: 'block', color: '#94a3b8', fontSize: '0.72rem', marginBottom: '3px' };
  const inpSt = { width: '100%', padding: '5px 8px', backgroundColor: '#0a0a0a', border: '1px solid #334155', borderRadius: '5px', color: '#e2e8f0', fontSize: '0.8rem', boxSizing: 'border-box', outline: 'none' };
  const otherScreens = screens.filter(s => s.id !== currentScreenId);
  const up = (key, val) => onChange({ ...props, [key]: val });
  const upOpt = (idx, field, val) => { const opts = [...(props.options || [])]; opts[idx] = { ...opts[idx], [field]: val }; onChange({ ...props, options: opts }); };
  const addOpt = () => onChange({ ...props, options: [...(props.options || []), { id: String(Date.now()), title: '' }] });
  const rmOpt = (idx) => onChange({ ...props, options: (props.options || []).filter((_, i) => i !== idx) });

  return (
    <div style={{ padding: '8px', background: 'rgba(56,189,248,0.05)', borderRadius: '6px', marginTop: '6px' }}>
      {['TextHeading', 'TextSubheading', 'TextBody'].includes(type) && (
        <div style={fieldSt}><label style={lblSt}>Texto</label><textarea value={props.text || ''} onChange={e => up('text', e.target.value)} style={{ ...inpSt, resize: 'vertical', fontFamily: 'inherit' }} rows={2} /></div>
      )}
      {type === 'Image' && (
        <>
          <div style={fieldSt}><label style={lblSt}>URL de imagen</label><input value={props.src || ''} onChange={e => up('src', e.target.value)} style={inpSt} placeholder="https://..." /></div>
          <div style={fieldSt}><label style={lblSt}>Altura (px)</label><input type="number" onWheel={e => e.currentTarget.blur()} value={props.height || 130} onChange={e => up('height', parseInt(e.target.value))} style={inpSt} /></div>
        </>
      )}
      {INPUT_TYPES.includes(type) && (
        <>
          <div style={fieldSt}><label style={lblSt}>Nombre (name)</label><input value={props.name || ''} onChange={e => up('name', e.target.value)} style={inpSt} placeholder="campo_unico" /></div>
          <div style={fieldSt}><label style={lblSt}>Etiqueta</label><input value={props.label || ''} onChange={e => up('label', e.target.value)} style={inpSt} /></div>
          <div style={fieldSt}>
            <label style={{ ...lblSt, display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input type="checkbox" checked={props.required !== false} onChange={e => up('required', e.target.checked)} style={{ accentColor: '#38bdf8' }} />
              Obligatorio
            </label>
          </div>
        </>
      )}
      {type === 'TextInput' && (
        <div style={fieldSt}>
          <label style={lblSt}>Tipo de entrada</label>
          <select value={props.input_type || 'text'} onChange={e => up('input_type', e.target.value)} style={{ ...inpSt, cursor: 'pointer' }}>
            <option value="text">Texto</option>
            <option value="email">Email</option>
            <option value="phone_number">Teléfono</option>
            <option value="number">Número</option>
          </select>
        </div>
      )}
      {['Dropdown', 'RadioButtonsGroup', 'CheckboxGroup'].includes(type) && (
        <div style={fieldSt}>
          <label style={lblSt}>Opciones</label>
          {(props.options || []).map((opt, i) => (
            <div key={i} style={{ display: 'flex', gap: '4px', marginBottom: '3px' }}>
              <input value={opt.id} onChange={e => upOpt(i, 'id', e.target.value)} style={{ ...inpSt, width: '70px' }} placeholder="id" />
              <input value={opt.title} onChange={e => upOpt(i, 'title', e.target.value)} style={{ ...inpSt, flex: 1 }} placeholder="Título" />
              <button onClick={() => rmOpt(i)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.9rem' }}>×</button>
            </div>
          ))}
          <button onClick={addOpt} style={{ background: 'transparent', border: '1px solid #38bdf8', color: '#38bdf8', borderRadius: '4px', fontSize: '0.72rem', padding: '2px 8px', cursor: 'pointer', marginTop: '2px' }}>+ Opción</button>
        </div>
      )}
      {type === 'Footer' && (
        <>
          <div style={fieldSt}><label style={lblSt}>Texto del botón</label><input value={props.label || 'Continuar'} onChange={e => up('label', e.target.value)} style={inpSt} /></div>
          <div style={fieldSt}>
            <label style={lblSt}>Acción al hacer clic</label>
            <select value={props.next_screen || ''} onChange={e => up('next_screen', e.target.value)} style={{ ...inpSt, cursor: 'pointer' }}>
              <option value="">Completar formulario</option>
              {otherScreens.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
            </select>
          </div>
        </>
      )}
    </div>
  );
}

function FormBuilder({ token }) {
  const isMobile = useIsMobile();
  const { id } = useParams();
  const navigate = useNavigate();
  const [formName, setFormName] = useState('');
  const [screens, setScreens] = useState([createScreen(0)]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [selectedCompId, setSelectedCompId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetch(`/api/forms/${id}/detail`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) {
          setFormName(d.name);
          if (d.config) {
            const cfg = typeof d.config === 'string' ? JSON.parse(d.config) : d.config;
            const loaded = (cfg.screens || []).map(s => ({
              id: s.id, title: s.title,
              components: extractComponentsFromScreen(s),
            }));
            if (loaded.length > 0) setScreens(loaded);
          }
        }
      })
      .finally(() => setLoading(false));
  }, [id, token]);

  const addComponent = (type) => {
    const def = COMPONENT_DEFS.find(d => d.type === type);
    const nc = { localId: `c_${Date.now()}`, type, props: { ...def.defaults } };
    setScreens(prev => prev.map((s, i) => i === activeIdx ? { ...s, components: [...s.components, nc] } : s));
    setSelectedCompId(nc.localId);
  };

  const removeComponent = (localId) => {
    setScreens(prev => prev.map((s, i) => i === activeIdx ? { ...s, components: s.components.filter(c => c.localId !== localId) } : s));
    if (selectedCompId === localId) setSelectedCompId(null);
  };

  const moveComponent = (localId, dir) => {
    setScreens(prev => prev.map((s, i) => {
      if (i !== activeIdx) return s;
      const comps = [...s.components];
      const idx = comps.findIndex(c => c.localId === localId);
      if (dir === 'up' && idx > 0) [comps[idx - 1], comps[idx]] = [comps[idx], comps[idx - 1]];
      if (dir === 'down' && idx < comps.length - 1) [comps[idx], comps[idx + 1]] = [comps[idx + 1], comps[idx]];
      return { ...s, components: comps };
    }));
  };

  const updateComponent = (localId, newProps) => {
    setScreens(prev => prev.map((s, i) => i === activeIdx ? { ...s, components: s.components.map(c => c.localId === localId ? { ...c, props: newProps } : c) } : s));
  };

  const addScreen = () => {
    const ns = createScreen(screens.length);
    setScreens(prev => [...prev, ns]);
    setActiveIdx(screens.length);
  };

  const removeScreen = (idx) => {
    if (screens.length <= 1) return;
    setScreens(prev => prev.filter((_, i) => i !== idx));
    setActiveIdx(prev => Math.min(prev, screens.length - 2));
  };

  const doUpload = async (saveOnly) => {
    if (saveOnly) setSaving(true); else setUploading(true);
    setMsg('');
    const config = generateFlowJson(screens);
    const variants = extractVariants(screens);
    try {
      const res = await fetch(`/api/forms/${id}/upload`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ config, variants, saveOnly }),
      });
      const d = await res.json();
      setMsg(res.ok ? (saveOnly ? '✓ Guardado' : '✓ Subido a Meta') : d.message || 'Error');
    } catch (err) { console.error('[FormBuilder] upload error:', err); setMsg('Error de conexión'); }
    finally { if (saveOnly) setSaving(false); else setUploading(false); }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 64px)' }}>
      <p style={{ color: theme.colors.textMuted }}>Cargando...</p>
    </div>
  );

  const activeScreen = screens[activeIdx];
  const panelBtnSt = { width: '100%', padding: '6px 10px', backgroundColor: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.15)', borderRadius: '6px', color: '#e2e8f0', cursor: 'pointer', fontSize: '0.78rem', textAlign: 'left', marginBottom: '3px' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', backgroundColor: theme.colors.background }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '0.5rem' : '1rem', padding: isMobile ? '0.6rem 0.8rem' : '0.6rem 1.5rem', backgroundColor: theme.colors.surface, borderBottom: `1px solid ${theme.colors.border}`, flexShrink: 0, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
        <button onClick={() => navigate('/formularios')} style={{ background: 'none', border: 'none', color: theme.colors.textMuted, cursor: 'pointer', fontSize: '0.9rem', padding: 0, flexShrink: 0 }}>← Volver</button>
        <span style={{ color: theme.colors.text, fontSize: '0.95rem', fontWeight: '600', flex: 1 }}>{formName}</span>
        {msg && <span style={{ color: msg.startsWith('✓') ? theme.colors.success : theme.colors.error, fontSize: '0.82rem', flexShrink: 0 }}>{msg}</span>}
        <button onClick={() => doUpload(true)} disabled={saving} style={{ padding: '7px 18px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`, borderRadius: '7px', color: theme.colors.text, cursor: saving ? 'not-allowed' : 'pointer', fontSize: '0.88rem', flexShrink: 0 }}>
          {saving ? 'Guardando...' : 'Guardar'}
        </button>
        <button onClick={() => doUpload(false)} disabled={uploading} style={{ padding: '7px 18px', backgroundColor: uploading ? theme.colors.border : theme.colors.primary, border: 'none', borderRadius: '7px', color: theme.colors.background, cursor: uploading ? 'not-allowed' : 'pointer', fontSize: '0.88rem', fontWeight: '700', flexShrink: 0 }}>
          {uploading ? 'Subiendo...' : 'Subir a Meta'}
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', flex: 1, overflow: 'hidden' }}>
        {/* Left panel */}
        <div style={{ width: isMobile ? '100%' : '195px', maxHeight: isMobile ? '38vh' : 'none', backgroundColor: theme.colors.surface, borderRight: isMobile ? 'none' : `1px solid ${theme.colors.border}`, borderBottom: isMobile ? `1px solid ${theme.colors.border}` : 'none', overflowY: 'auto', padding: '0.75rem', flexShrink: 0, boxSizing: 'border-box' }}>
          {[['display', 'Textos e imágenes'], ['input', 'Campos de entrada'], ['action', 'Acción']].map(([cat, label]) => (
            <div key={cat}>
              <p style={{ color: theme.colors.textMuted, fontSize: '0.65rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', margin: cat === 'display' ? '0 0 5px' : '10px 0 5px' }}>{label}</p>
              {COMPONENT_DEFS.filter(d => d.category === cat).map(d => (
                <button key={d.type} onClick={() => addComponent(d.type)} style={panelBtnSt}>+ {d.label}</button>
              ))}
            </div>
          ))}
        </div>

        {/* Main area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Screen tabs */}
          <div style={{ display: 'flex', gap: '4px', padding: '8px 12px', backgroundColor: theme.colors.background, borderBottom: `1px solid ${theme.colors.border}`, flexShrink: 0, alignItems: 'center', flexWrap: 'wrap' }}>
            {screens.map((s, idx) => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                <button onClick={() => setActiveIdx(idx)} style={{ padding: '5px 12px', borderRadius: '6px', border: `1px solid ${activeIdx === idx ? theme.colors.primary : theme.colors.border}`, backgroundColor: activeIdx === idx ? `${theme.colors.primary}22` : 'transparent', color: activeIdx === idx ? theme.colors.primary : theme.colors.text, cursor: 'pointer', fontSize: '0.82rem', fontWeight: activeIdx === idx ? '700' : '400' }}>{s.title}</button>
                {screens.length > 1 && <button onClick={() => removeScreen(idx)} style={{ background: 'none', border: 'none', color: theme.colors.textMuted, cursor: 'pointer', fontSize: '0.75rem', padding: '0 2px' }}>×</button>}
              </div>
            ))}
            <button onClick={addScreen} style={{ padding: '5px 10px', borderRadius: '6px', border: `1px solid ${theme.colors.border}`, backgroundColor: 'transparent', color: theme.colors.textMuted, cursor: 'pointer', fontSize: '0.82rem' }}>+ Pantalla</button>
          </div>

          {/* Canvas */}
          <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '1rem 0.8rem' : '1.5rem' }}>
            <div style={{ maxWidth: '480px', margin: '0 auto' }}>
              {activeScreen.components.length === 0 && (
                <div style={{ border: `2px dashed ${theme.colors.border}`, borderRadius: '10px', padding: '2rem', textAlign: 'center', color: theme.colors.textMuted, fontSize: '0.88rem' }}>
                  Haz clic en un componente del panel izquierdo para agregarlo aquí.
                </div>
              )}
              {activeScreen.components.map((comp, idx) => {
                const def = COMPONENT_DEFS.find(d => d.type === comp.type);
                const isSel = selectedCompId === comp.localId;
                const total = activeScreen.components.length;
                return (
                  <div key={comp.localId} style={{ backgroundColor: theme.colors.surface, border: `1px solid ${isSel ? theme.colors.primary : theme.colors.border}`, borderRadius: '8px', padding: '10px 12px', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: theme.colors.primary, fontSize: '0.75rem', fontWeight: '700' }}>{def?.label || comp.type}</span>
                      <div style={{ display: 'flex', gap: '3px' }}>
                        {[['↑', 'up', idx === 0], ['↓', 'down', idx === total - 1]].map(([arrow, dir, dis]) => (
                          <button key={dir} onClick={() => moveComponent(comp.localId, dir)} disabled={dis} style={{ background: 'none', border: 'none', color: dis ? theme.colors.textMuted : theme.colors.text, cursor: dis ? 'not-allowed' : 'pointer', fontSize: '0.8rem', padding: '0 3px' }}>{arrow}</button>
                        ))}
                        <button onClick={() => setSelectedCompId(isSel ? null : comp.localId)} style={{ background: 'none', border: 'none', color: isSel ? theme.colors.primary : theme.colors.textMuted, cursor: 'pointer', fontSize: '0.8rem', padding: '0 3px' }}>✏️</button>
                        <button onClick={() => removeComponent(comp.localId)} style={{ background: 'none', border: 'none', color: theme.colors.error, cursor: 'pointer', fontSize: '0.8rem', padding: '0 3px' }}>×</button>
                      </div>
                    </div>
                    <div style={{ color: theme.colors.textMuted, fontSize: '0.78rem', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {comp.props.text || comp.props.label || comp.props.name || comp.type}
                    </div>
                    {isSel && <PropsPanel comp={comp} onChange={(p) => updateComponent(comp.localId, p)} screens={screens} currentScreenId={activeScreen.id} />}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FormBuilder;
